/**
 * Hook for finding chunk data for URL citations
 */

import type { ChatAppResponse } from "@/api/models"

import { getCitationSourceForLookup } from "@/utils/citation-utils"

import { isResponseContextDataPointArray, isStringArray } from "../utils/type-guards"
import {
  extractWebContentChunk,
  isWebContentItem,
  normalizeTextFragmentUrl,
} from "../utils/url-utils"

type ChunkData = {
  text: string[]
  title?: string
  images?: { url: string }[]
}

/**
 * Extract the content portion from chunk text for highlighting.
 * Chunk format is "citation_source: content" — we strip the source prefix.
 */
export function extractChunkTextForHighlight(
  chunk: ChunkData | null,
  citation: string
): string | null {
  const raw = chunk?.text[0]
  if (!raw || typeof raw !== "string") return null
  const baseCitation = getCitationSourceForLookup(citation)
  const prefix = baseCitation + ":"
  if (raw.startsWith(prefix)) {
    return raw.substring(prefix.length).trim()
  }
  return raw
}

type WebContentItem =
  | string
  | {
      link?: string
      url?: string
      contents?: unknown
      content?: unknown
      snippet?: unknown
      title?: unknown
    }

/**
 * Extract id and content from <document> XML emitted by the backend.
 */
function extractDocumentXmlChunk(
  xml: string,
  baseCitation: string
): { text: string; title?: string } | null {
  const trimmed = (xml || "").trim()
  if (!trimmed || !trimmed.includes("<document")) {
    return null
  }

  const idMatch = trimmed.match(/<document[^>]*\bid\s*=\s*["']([^"']+)["']/i)
  if (!idMatch) {
    return null
  }

  const docId = idMatch[1].trim()
  const normalizedDocSource = docId.split("#page=")[0].replace(/#$/, "")
  if (normalizedDocSource !== baseCitation) {
    return null
  }

  const contentMatch = trimmed.match(/<content[^>]*>([\s\S]*?)<\/content>/i)
  const captionsMatch = trimmed.match(/<captions[^>]*>([\s\S]*?)<\/captions>/i)
  const content = (contentMatch?.[1] ?? captionsMatch?.[1] ?? "").trim()
  if (!content) {
    return null
  }

  const titleMatch = trimmed.match(/<title[^>]*>([\s\S]*?)<\/title>/i)
  const filenameMatch = trimmed.match(/<filename[^>]*>([\s\S]*?)<\/filename>/i)
  const title = (titleMatch?.[1] ?? filenameMatch?.[1] ?? "").trim() || undefined
  return { text: content, title }
}

function findChunkInTextArray(
  textArray: string[],
  baseCitation: string,
  cleanCitation: string
): ChunkData | null {
  const exactChunk = textArray.find((text) => text.startsWith(baseCitation + ":"))
  if (exactChunk) {
    return { text: [exactChunk] }
  }

  for (const text of textArray) {
    if (text.trim().startsWith("<document")) {
      const docEntry = extractDocumentXmlChunk(text, baseCitation)
      if (docEntry) {
        return { text: [docEntry.text], title: docEntry.title }
      }
    }
  }

  for (const text of textArray) {
    const webEntry = extractWebContentChunk(text, cleanCitation)
    if (webEntry) {
      return { text: [webEntry.text], title: webEntry.title }
    }
  }

  return null
}

function findChunkInDataPoint(
  dataPoint: { text: unknown[]; title?: string; images?: { url: string }[] },
  baseCitation: string,
  cleanCitation: string
): ChunkData | null {
  const textArray = dataPoint.text.filter((item): item is string => typeof item === "string")
  const found = findChunkInTextArray(textArray, baseCitation, cleanCitation)
  if (found) {
    return { ...found, title: found.title ?? dataPoint.title, images: dataPoint.images }
  }

  for (const item of dataPoint.text) {
    if (!isWebContentItem(item)) {
      continue
    }
    const webEntry = extractWebContentChunk(item, cleanCitation)
    if (webEntry) {
      return {
        text: [webEntry.text],
        title: webEntry.title ?? dataPoint.title,
        images: dataPoint.images,
      }
    }
  }

  return null
}

/**
 * Find chunk data for a URL citation from the answer context
 */
export const useFindChunkForCitation = (answer?: ChatAppResponse) => {
  const findChunkForUrlCitation = (citation: string): ChunkData | null => {
    if (!answer?.context.data_points) {
      return null
    }
    const dataPoints = answer.context.data_points

    const normalizedCitation = normalizeTextFragmentUrl(citation)
    const cleanCitation = normalizedCitation.split("#:~:text=")[0]
    const baseCitation = getCitationSourceForLookup(cleanCitation)

    if (isResponseContextDataPointArray(dataPoints)) {
      for (const dataPoint of dataPoints) {
        const found = findChunkInDataPoint(dataPoint, baseCitation, cleanCitation)
        if (found) return found
      }
    } else if (isStringArray(dataPoints)) {
      const found = findChunkInTextArray(dataPoints, baseCitation, cleanCitation)
      if (found) return found
    } else if (typeof dataPoints === "object") {
      const record = dataPoints as {
        text?: unknown
        images?: { url: string }[]
        web_content?: unknown
      }

      const textArray = Array.isArray(record.text)
        ? record.text.filter((item): item is string => typeof item === "string")
        : []
      const found = findChunkInTextArray(textArray, baseCitation, cleanCitation)
      if (found) {
        return { ...found, images: record.images }
      }

      const webContentItems = Array.isArray(record.web_content)
        ? (record.web_content as WebContentItem[])
        : []
      for (const item of webContentItems) {
        const webEntry = extractWebContentChunk(item, cleanCitation)
        if (webEntry) {
          return {
            text: [webEntry.text],
            title: webEntry.title,
            images: record.images,
          }
        }
      }
    }

    return null
  }

  return { findChunkForUrlCitation }
}
